import {
  getLocalTimeZone,
  toCalendarDate,
  today,
} from "@internationalized/date";
import { trainFormGroup } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { dateTimeSchemaForm } from "shared/form/dateTimeSchema";
import { z } from "zod";

const batchTrainFormSchema = z.array(trainFormGroup);

export type BatchTrainForm = z.infer<typeof batchTrainFormSchema>;

const groupTrainFormSchema = z.object({
  // This information is also available in batchTrainForm.[0].trainIdsInGroup, however this simplifies getting and updating trainIds
  selectedTrains: z.string().array(),
  // array is used in order to be able to fill for multiple directions per group
  batchTrainForm: batchTrainFormSchema,
});

export type GroupTrainForm = z.infer<typeof groupTrainFormSchema>;

const baseDurationSchema = z.object({
  type: z.literal("TIMED"),
  from_time: dateTimeSchemaForm,
  to_time: dateTimeSchemaForm,
});

// In edit mode the duration fields are disabled/uneditable, so "today"-relative
// checks would otherwise fail once time has passed since the deviation was created.
const withDurationRefinements = (isEditMode: boolean) =>
  baseDurationSchema
    .refine(
      (data) =>
        isEditMode ||
        (data.from_time &&
          toCalendarDate(data.from_time) >= today(getLocalTimeZone())),
      {
        message: "Startdato kan ikke være før i dag",
        path: ["from_time"],
      },
    )
    .refine(
      (data) =>
        isEditMode ||
        (data.from_time &&
          toCalendarDate(data.from_time) <=
            today(getLocalTimeZone()).add({ days: 13 })),
      {
        message: "Startdato kan ikke være mer enn 14 dager fra i dag",
        path: ["from_time"],
      },
    )
    .refine((data) => data.to_time >= data.from_time, {
      message: "Sluttdato kan ikke være før startdato",
      path: ["to_time"],
    })
    .refine(
      (data) =>
        isEditMode ||
        (data.to_time &&
          toCalendarDate(data.to_time) <=
            today(getLocalTimeZone()).add({ days: 13 })),
      {
        message: "Sluttdato kan ikke være mer enn 14 dager fra i dag",
        path: ["to_time"],
      },
    )
    .refine(
      (data) =>
        data.from_time &&
        data.to_time &&
        toCalendarDate(data.from_time).add({ days: 4 }) >=
          toCalendarDate(data.to_time),
      {
        message: "Maks 5 dager kan behandles om gangen",
        path: ["to_time"],
      },
    );

export const getFormSchema = (isEditMode: boolean) =>
  z.object({
    productionDeviationForm: z.record(groupTrainFormSchema),
    durations: withDurationRefinements(isEditMode),
  });

export const formSchema = getFormSchema(false);

export type ProductionDeviationForm = z.infer<typeof formSchema>;
